import type { RawDealItemForValidation } from '@/lib/workers/validation.worker';
import {
  DealItemWithVersions,
  GetDealItemsByDealIdQuery,
} from '@/lib/graphql/generated/graphql';

export type DealItemFromQuery = NonNullable<
  GetDealItemsByDealIdQuery['deal']
>['dealItemsWithVersions'][number];

const priceKeys = [
  'buyingPrice',
  'choosePrice',
  'discountedPrice',
  'listPrice',
  'potentialSalesVolume',
  'shippingPrice',
  'eeePrice',
] as const;

type PriceKey = (typeof priceKeys)[number];

const percentageKeys = [
  'chooseDiscount',
  'marginPercentage',
  'vatRate',
] as const;

type PercentageKey = (typeof percentageKeys)[number];

export function normalize(items: DealItemFromQuery[]): DealItemWithVersions[] {
  return items.map((item) => {
    const activeVersionCopy: DealItemFromQuery['activeVersion'] = {
      ...item.activeVersion,
    };
    const lastBrandVersionCopy: DealItemFromQuery['lastBrandVersion'] =
      item.lastBrandVersion
        ? {
            ...item.lastBrandVersion,
          }
        : null;
    const lastChooseVersionCopy: DealItemFromQuery['lastChooseVersion'] =
      item.lastChooseVersion
        ? {
            ...item.lastChooseVersion,
          }
        : null;

    priceKeys.forEach((k: PriceKey) => {
      const activeVal = item.activeVersion[k];
      if (typeof activeVal === 'number') activeVersionCopy[k] = activeVal / 100;
      if (lastBrandVersionCopy) {
        const brandVal = lastBrandVersionCopy[k];
        if (typeof brandVal === 'number')
          lastBrandVersionCopy[k] = brandVal / 100;
      }
      if (lastChooseVersionCopy) {
        const chooseVal = lastChooseVersionCopy[k];
        if (typeof chooseVal === 'number')
          lastChooseVersionCopy[k] = chooseVal / 100;
      }
    });

    percentageKeys.forEach((k: PercentageKey) => {
      const activeVal = item.activeVersion[k];
      if (typeof activeVal === 'number') activeVersionCopy[k] = activeVal * 100;
      if (lastBrandVersionCopy) {
        const brandVal = lastBrandVersionCopy[k];
        if (typeof brandVal === 'number')
          lastBrandVersionCopy[k] = brandVal * 100;
      }
      if (lastChooseVersionCopy) {
        const chooseVal = lastChooseVersionCopy[k];
        if (typeof chooseVal === 'number')
          lastChooseVersionCopy[k] = chooseVal * 100;
      }
    });

    return {
      ...item,
      activeVersion: {
        ...activeVersionCopy,
        updatingFields: item.activeVersion.updatingFields,
        validationErrors: item.activeVersion.validationErrors ?? [],
      },
      lastBrandVersion: lastBrandVersionCopy,
      lastChooseVersion: lastChooseVersionCopy,
    } as DealItemWithVersions;
  });
}

export function normalizeSingle(item: DealItemFromQuery): DealItemWithVersions {
  return normalize([item])[0];
}

/**
 * Merge partagé des deux chemins d'ingestion (batch SSE et subscription) :
 * conserve les dernières versions brand/choose connues quand l'entrant est
 * null, et préserve l'activeVersion d'une édition optimiste en attente tout
 * en prenant les validationErrors fraîchement reçues.
 */
export function mergeStreamedItem(
  normalized: DealItemWithVersions,
  prevItem: DealItemWithVersions | undefined,
  pendingIds: ReadonlySet<string>
): DealItemWithVersions {
  let merged: DealItemWithVersions = {
    ...normalized,
    lastBrandVersion:
      normalized.lastBrandVersion ?? prevItem?.lastBrandVersion ?? null,
    lastChooseVersion:
      normalized.lastChooseVersion ?? prevItem?.lastChooseVersion ?? null,
  };

  if (pendingIds.has(normalized.id) && prevItem) {
    merged = {
      ...merged,
      activeVersion: {
        ...merged.activeVersion,
        ...prevItem.activeVersion,
        validationErrors: normalized.activeVersion.validationErrors,
      },
    };
  }

  return merged;
}

/**
 * Projette un item brut (valeurs en cents, post-convertDateFields) vers la
 * forme minimale attendue par le worker de validation, pour ne pas retenir
 * le graphe complet des items pendant toute la durée du stream.
 */
export function toRawValidationItem(
  item: DealItemWithVersions
): RawDealItemForValidation {
  const av = item.activeVersion;
  return {
    id: item.id,
    activeVersion: {
      historyId: av.historyId,
      isSelected: av.isSelected,
      name: av.name,
      sku: av.sku,
      gtin: av.gtin,
      options: av.options,
      shopifyVariantId: av.shopifyVariantId,
      hasDescription: av.hasDescription,
      images: av.images,
      productCategory: av.productCategory,
      listPrice: av.listPrice,
      discountedPrice: av.discountedPrice,
      chooseDiscount: av.chooseDiscount,
      choosePrice: av.choosePrice,
      buyingPrice: av.buyingPrice,
      shippingPrice: av.shippingPrice,
      marginPercentage: av.marginPercentage,
      stockQuantity: av.stockQuantity,
      sourceUrl: av.sourceUrl,
      madeIn: av.madeIn,
      productType: av.productType,
      couponExpirationDate: av.couponExpirationDate,
      couponTerms: av.couponTerms,
      couponHowToUse: av.couponHowToUse,
    },
  };
}
